import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import MemberProfile from './MemberProfile'; 
import Violations from './ViolationsbyMember';
import api, { API_BASE_URL } from "../../src/utils/api";

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const limit = 20;

const ClubMembers = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMembers(currentPage);
  }, [currentPage]);

  useEffect(() => {
    const filtered = members.filter(member => 
      member.FirstName?.toLowerCase().includes(searchTerm) ||
      member.LastName?.toLowerCase().includes(searchTerm) ||
      member.ClubMemberCode?.toString().includes(searchTerm)
    );
    setFilteredMembers(filtered);
  }, [searchTerm, members]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchMembers = async (page, { force = false } = {}) => {
    const cacheKey = `members_page_${page}`;

    // If not forcing, try cache first
    if (!force) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        if (now - parsed.timestamp < CACHE_DURATION) {
          setMembers(parsed.members);
          setFilteredMembers(parsed.members);
          setTotalPages(parsed.totalPages);
          return;
        } else {
          localStorage.removeItem(cacheKey);
        }
      }
    } else {
      // optional: clear before fetching
      localStorage.removeItem(cacheKey);
    }

    setLoading(true);
    try {
      const response = await api.get(`/members?page=${page}&limit=${limit}`);
      // axios puts your payload on response.data
      const data = response.data;

      setMembers(data.members);
      setFilteredMembers(data.members);
      setTotalPages(data.totalPages);

      localStorage.setItem(cacheKey, JSON.stringify({
        members: data.members,
        totalPages: data.totalPages,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentPage = () => {
    fetchMembers(currentPage, { force: true });
  };

  const changePage = (direction) => {
    const newPage = currentPage + direction;
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const openProfileModal = (member) => {
    setIsModalOpen(true);
    setSelectedMember(member);
  };

  const closeProfileModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  return (
    <div>
      <h1>Club Members</h1>

      <div className="search-container" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          id="searchInput"
          onChange={handleSearch}
          placeholder="Search for members..."
        />

        <button onClick={refreshCurrentPage} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="table-container">
        {loading ? (
          <p>Loading members...</p>
        ) : filteredMembers.length > 0 ? (
          <>
            <table id="membersTable">
              <thead>
                <tr>
                  <th>CompanyCode</th>
                  <th>ClubMemberCode</th>
                  <th>Title</th>
                  <th>Name</th>
                  <th>Familiar Name</th>
                  <th>Suffix</th>
                  <th>Gender</th>
                  <th>BirthDate</th>
                  <th>Join Date</th>
                  <th>Club Status</th>
                  <th>BillToClubMemberCode</th>
                  <th>Photo</th>
                  <th>FullAddress</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map(member => (
                  <tr key={member.ClubMemberCode}>
                    <td>{member.CompanyCode}</td>
                    <td>
                      <button
                        className="member-button"
                        onClick={() => openProfileModal(member)}
                      >
                        {member.ClubMemberCode}
                      </button>
                    </td>
                    <td>{member.Title}</td>
                    <td>{`${member.FirstName} ${member.LastName}`}</td>
                    <td>{member.FamiliarName}</td>
                    <td>{member.Suffix}</td>
                    <td>{member.Gender}</td>
                    <td>{member.BirthDate ? new Date(member.BirthDate).toLocaleDateString() : ''}</td>
                    <td>{member.JoinDate ? new Date(member.JoinDate).toLocaleDateString() : ''}</td>
                    <td>{member.ClubStatusRuleCode}</td>
                    <td>{member.BillToClubMemberCode}</td>
                    <td>
                      <img
                        src={`${API_BASE_URL}/images/${encodeURIComponent(member.PhotoGraphicFileName?.trim() || '')}`}
                        alt="Member"
                        onError={(e) => (e.currentTarget.src = '/img/100x100.png')}
                        style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                      />
                    </td>
                    <td>{member.FullAddress}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination">
              <button onClick={() => changePage(-1)} disabled={currentPage === 1}>
                &#x276E;
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={() => changePage(1)} disabled={currentPage === totalPages}>
                &#x276F;
              </button>
            </div>
          </>
        ) : (
          <p>No members found</p>
        )}
      </div>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={closeProfileModal}>
          <div className="tab-container">
            <button 
              onClick={() => setActiveTab('info')} 
              className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
            >
              Info
            </button>
            <button 
              onClick={() => setActiveTab('Violations')} 
              className={`tab-button ${activeTab === 'Violations' ? 'active' : ''}`}
            >
              Violations
            </button>
          </div>
          {activeTab === 'info' && <MemberProfile member={selectedMember} />}
          {activeTab === 'Violations' && <Violations member={selectedMember} />}
        </Modal>
      )}
    </div>
  );
};

export default ClubMembers;
